import { Point, Polygon } from '../../context/context';
import * as turf from '@turf/turf';
import { logEvent } from '../analytics';
import { 
  formatAddressFromComponents, 
  extractGoogleComponents,
  sanitizeAddress,
  detectIntersection,
  extractIntersectionName
} from '../../utils/addressUtils';
import { getAtlasApiBaseUrl } from '../getAtlasApiBaseUrl';
import { atlasFetch } from '../api/atlasBackendClient';

/**
 * OpenStreetMap API Service for transit infrastructure data
 * Implements Overpass API queries for transit-related features
 */
export interface OSMTransitData {
  stops: {
    bus: OSMStop[];
    train: OSMStop[];
    tram: OSMStop[];
    subway: OSMStop[];
  };
  routes: {
    bus: OSMRoute[];
    train: OSMRoute[];
    tram: OSMRoute[];
    subway: OSMRoute[];
  };
  infrastructure: {
    stations: OSMStation[];
    platforms: OSMPlatform[];
    shelters: OSMShelter[];
  };
  coverage: {
    stopDensity: number; // stops per km²
    routeDensity: number; // route km per km²
    accessibilityScore: number; // 0-1 score
  };
}

export interface OSMStop {
  id: string;
  name: string;
  type: 'bus' | 'train' | 'tram' | 'subway';
  coordinates: [number, number];
  routes: string[];
  wheelchair: boolean;
  shelter: boolean;
  realtime: boolean;
}

export interface OSMRoute {
  id: string;
  name: string;
  type: 'bus' | 'train' | 'tram' | 'subway';
  operator: string;
  frequency: number; // trips per hour
  stops: string[];
  geometry: [number, number][];
  // Optional PTv2 fields. Coverage is spotty in practice — undefined is normal.
  ref?: string;
  from?: string;
  to?: string;
  via?: string;
  network?: string;
  colour?: string; // normalized hex (#rrggbb) when recognized; otherwise undefined
  textColour?: string;
  openingHours?: string;
  interval?: string; // typically minutes ("10") or HH:MM ("00:10")
  duration?: string;
  wheelchair?: 'yes' | 'no' | 'limited' | 'unknown';
  routeMasterId?: string;
  // The original OSM relation tag (`bus|tram|subway|train|light_rail|trolleybus`)
  // before we collapse light_rail → train and trolleybus → bus for the buckets.
  osmMode?: 'bus' | 'train' | 'tram' | 'subway' | 'light_rail' | 'trolleybus';
}

export interface OSMStation {
  id: string;
  name: string;
  type: 'bus' | 'train' | 'tram' | 'subway';
  coordinates: [number, number];
  platforms: number;
  accessibility: boolean;
}

export interface OSMPlatform {
  id: string;
  stationId: string;
  coordinates: [number, number];
  accessibility: boolean;
}

export interface OSMShelter {
  id: string;
  stopId: string;
  coordinates: [number, number];
  covered: boolean;
}

export interface OSMConfig {
  baseUrl: string;
  rateLimit: {
    requestsPerMinute: number;
    requestsPerHour: number;
  };
  cache: {
    ttl: number; // Time to live in milliseconds
    maxSize: number; // Maximum number of cached items
  };
  timeout: number; // Request timeout in milliseconds
}

// — module-private helpers for PTv2 route parsing —

/**
 * Chains an ordered list of way geometries into a single polyline. Detects
 * reversed ways by checking shared endpoints with the previously-appended
 * segment, flips when needed, and avoids duplicating the shared join point.
 * Returns a flat `[lat, lng]` array. Out-of-order or disjoint ways still
 * concatenate, but the result may zigzag — that's fine for visualization.
 */
function chainOsmWayGeometries(ways: [number, number][][]): [number, number][] {
  if (ways.length === 0) return [];
  const eq = (a: [number, number], b: [number, number]) =>
    Math.abs(a[0] - b[0]) < 1e-9 && Math.abs(a[1] - b[1]) < 1e-9;

  const out: [number, number][] = [...ways[0]];
  for (let i = 1; i < ways.length; i++) {
    const next = ways[i];
    if (next.length === 0) continue;
    const last = out[out.length - 1];
    let segment = next;
    if (!eq(last, next[0])) {
      // Try flipping the next way if its end matches the running tail.
      if (eq(last, next[next.length - 1])) {
        segment = [...next].reverse();
      } else {
        // Neither end matches — append anyway (will produce a small jump).
      }
    }
    // Skip the duplicate join point when endpoints align.
    if (eq(out[out.length - 1], segment[0])) {
      out.push(...segment.slice(1));
    } else {
      out.push(...segment);
    }
  }
  return out;
}

const OSM_NAMED_COLOURS: Record<string, string> = {
  red: '#e53935', blue: '#1e88e5', green: '#43a047', yellow: '#fdd835',
  orange: '#fb8c00', purple: '#8e24aa', brown: '#6d4c41', black: '#212121',
  white: '#fafafa', gray: '#757575', grey: '#757575', pink: '#ec407a',
  cyan: '#00acc1', magenta: '#d81b60',
};

/**
 * Normalizes an OSM `colour` tag to `#rrggbb`. Accepts `#rgb`, `rgb`,
 * `#rrggbb`, `rrggbb`, and a small set of CSS named colours. Returns
 * undefined for anything else so callers can apply a per-mode default.
 */
function normalizeOsmColour(raw?: string): string | undefined {
  if (!raw) return undefined;
  const v = raw.trim().toLowerCase();
  if (!v) return undefined;
  if (OSM_NAMED_COLOURS[v]) return OSM_NAMED_COLOURS[v];
  const hex = v.startsWith('#') ? v.slice(1) : v;
  if (/^[0-9a-f]{3}$/.test(hex)) {
    return '#' + hex.split('').map(c => c + c).join('');
  }
  if (/^[0-9a-f]{6}$/.test(hex)) {
    return '#' + hex;
  }
  return undefined;
}

/**
 * Reverse lookup helper: given a child relation id, returns the id of the
 * first route_master that lists it as a child (or undefined). Currently used
 * only to populate `routeMasterId` on the emitted route for telemetry.
 */
function routeMasterIdFor(
  childId: number,
  routeMasterTagsByChildId: Map<number, Record<string, string>>,
): string | undefined {
  if (!routeMasterTagsByChildId.has(childId)) return undefined;
  // The map was built from each route_master's members; the tags object is
  // shared so we can't recover the master id from the map directly. The
  // tags include `name` which is good enough as a fallback identifier.
  const tags = routeMasterTagsByChildId.get(childId)!;
  return tags.ref || tags.name;
}

export class OpenStreetMapService {
  private baseUrl: string;
  private fallbackBaseUrls: string[] = [
    'https://overpass.kumi.systems',
    'https://lz4.overpass-api.de',
  ];
  private cache = new Map<string, { data: OSMTransitData | string; timestamp: number }>();
  private rateLimitTracker = {
    requestsThisMinute: 0,
    requestsThisHour: 0,
    lastMinuteReset: Date.now(),
    lastHourReset: Date.now(),
  };
  private rateLimit: OSMConfig['rateLimit'];
  private cacheConfig: OSMConfig['cache'];
  private timeout: number;

  constructor(config: OSMConfig) {
    this.baseUrl = config.baseUrl;
    this.rateLimit = config.rateLimit;
    this.cacheConfig = config.cache;
    this.timeout = config.timeout;
  }

  /**
   * Get transit infrastructure data for a polygon area
   */
  async getTransitData(polygon: Polygon): Promise<OSMTransitData> {
    const startTime = Date.now();
    const cacheKey = this.generateCacheKey(polygon);
    
    // Check cache first
    const cached = this.getTransitDataFromCache(cacheKey);
    if (cached) {
      logEvent({
        category: 'Transit Analysis',
        action: 'OSMTransitDataCacheHit',
        label: polygon.name || 'Unnamed Polygon',
        polygon_id: polygon.id,
        cache_key: cacheKey,
        response_time_ms: Date.now() - startTime
      });
      
      console.log('OSM transit data retrieved from cache');
      return cached;
    }

    // Check rate limits
    await this.checkRateLimits();

    try {
      // Extract polygon bounds for API request
      const bounds = this.calculatePolygonBounds(polygon);
      
      // Check if polygon is too large and needs optimization
      const area = this.calculatePolygonArea(polygon);
      const isLargePolygon = area > 100; // 100 km² threshold
      
      let data: OSMTransitData;
      
      if (isLargePolygon) {
        console.log(`Large polygon detected (${area.toFixed(1)} km²), using optimized query`);
        data = await this.fetchTransitDataOptimized(polygon, bounds);
      } else {
        data = await this.fetchTransitData(bounds);
      }
      
      // --- Filter stops to only those inside the polygon (not just bounding box) ---
      // Fix: Convert all rings to [lng, lat] and ensure closure for Turf.js
      const geojsonRings = polygon.coordinates.map(ring => {
        const converted = ring.map(([lat, lng]) => [lng, lat]);
        // Ensure ring is closed
        if (
          converted.length < 4 ||
          converted[0][0] !== converted[converted.length - 1][0] ||
          converted[0][1] !== converted[converted.length - 1][1]
        ) {
          converted.push([...converted[0]]);
        }
        return converted;
      });
      const turfPoly = turf.polygon(geojsonRings);
      const filterStops = (stops: OSMStop[]) => stops.filter(stop =>
        turf.booleanPointInPolygon(
          turf.point([stop.coordinates[1], stop.coordinates[0]]),
          turfPoly
        )
      );
      data.stops.bus = filterStops(data.stops.bus);
      data.stops.train = filterStops(data.stops.train);
      data.stops.tram = filterStops(data.stops.tram);
      data.stops.subway = filterStops(data.stops.subway);
      
      // Cache the result
      this.setCache(cacheKey, data);
      
      const endTime = Date.now();
      const duration = endTime - startTime;
      
      logEvent({
        category: 'Transit Analysis',
        action: 'OSMTransitDataFetched',
        label: polygon.name || 'Unnamed Polygon',
        polygon_id: polygon.id,
        polygon_area_km2: area,
        is_large_polygon: isLargePolygon,
        total_stops: Object.values(data.stops).flat().length,
        bus_stops: data.stops.bus.length,
        train_stops: data.stops.train.length,
        tram_stops: data.stops.tram.length,
        subway_stops: data.stops.subway.length,
        cache_key: cacheKey,
        response_time_ms: duration
      });
      
      console.log('OSM transit data fetched successfully');
      return data;
    } catch (error) {
      const endTime = Date.now();
      const duration = endTime - startTime;
      
      logEvent({
        category: 'Transit Analysis',
        action: 'OSMTransitDataError',
        label: polygon.name || 'Unnamed Polygon',
        polygon_id: polygon.id,
        error_message: error instanceof Error ? error.message : 'Unknown error',
        response_time_ms: duration
      });
      
      console.error('Error fetching OSM transit data:', error);
      throw new Error(`Failed to fetch transit data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Fetch PTv2 transit routes within the polygon. Returns just the routes
   * portion of OSMTransitData (stops/infrastructure are empty arrays since
   * this query intentionally doesn't ask for them). Run in parallel with
   * `getTransitData` when the caller wants to stream stops first and let
   * the heavier routes query complete asynchronously.
   */
  async getTransitRoutes(polygon: Polygon): Promise<OSMTransitData['routes']> {
    const startTime = Date.now();
    await this.checkRateLimits();
    const bounds = this.calculatePolygonBounds(polygon);
    const query = this.buildRouteOverpassQuery(bounds);
    const baseUrls = Array.from(new Set([this.baseUrl, ...this.fallbackBaseUrls]));
    const errors: string[] = [];
    for (const baseUrl of baseUrls) {
      const url = `${baseUrl.replace(/\/+$/, '')}/api/interpreter`;
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Accept': 'application/json',
          },
          body: `data=${encodeURIComponent(query)}`,
          // Route queries can take much longer than the default stops timeout.
          signal: AbortSignal.timeout(Math.max(this.timeout, 120000)),
        });
        if (!response.ok) {
          errors.push(`${url} -> ${response.status} ${response.statusText}`);
          continue;
        }
        const rawData = await response.json();
        const data = this.transformOSMResponse(rawData);
        console.log('OSM routes fetched:', {
          bus: data.routes.bus.length,
          tram: data.routes.tram.length,
          subway: data.routes.subway.length,
          train: data.routes.train.length,
          ms: Date.now() - startTime,
        });
        return data.routes;
      } catch (error) {
        errors.push(`${url} -> ${error instanceof Error ? error.message : 'Unknown network error'}`);
      }
    }
    throw new Error(`OSM routes API failed across all endpoints: ${errors.join(' | ')}`);
  }

  /**
   * Calculate polygon area in square kilometers
   */
  private calculatePolygonArea(polygon: Polygon): number {
    // Simple approximation using bounding box area
    const bounds = this.calculatePolygonBounds(polygon);
    const latDiff = bounds.maxLat - bounds.minLat;
    const lngDiff = bounds.maxLng - bounds.minLng;
    
    // Convert to approximate km² (rough approximation)
    const latKm = latDiff * 111; // 1 degree lat ≈ 111 km
    const lngKm = lngDiff * 111 * Math.cos((bounds.minLat + bounds.maxLat) * Math.PI / 360);
    
    return latKm * lngKm;
  }

  /**
   * Fetch transit data with optimization for large polygons
   */
  private async fetchTransitDataOptimized(
    polygon: Polygon,
    bounds: { minLat: number; maxLat: number; minLng: number; maxLng: number }
  ): Promise<OSMTransitData> {
    // Split large polygon into smaller chunks
    const chunks = this.splitPolygonIntoChunks(polygon, 4); // Split into 4 chunks
    
    console.log(`Splitting large polygon into ${chunks.length} chunks for optimized querying`);
    
    // Fetch data for each chunk in parallel (with rate limiting)
    const chunkPromises = chunks.map(async (chunk, index) => {
      // Add delay between chunks to respect rate limits
      if (index > 0) {
        await new Promise(resolve => setTimeout(resolve, 1000)); // 1 second delay
      }
      
      const chunkBounds = this.calculatePolygonBounds(chunk);
      return this.fetchTransitData(chunkBounds);
    });
    
    const chunkResults = await Promise.all(chunkPromises);
    
    // Merge results from all chunks
    return this.mergeTransitData(chunkResults);
  }

  /**
   * Split polygon into smaller chunks for optimized querying
   */
  private splitPolygonIntoChunks(polygon: Polygon, numChunks: number): Polygon[] {
    const bounds = this.calculatePolygonBounds(polygon);
    const latStep = (bounds.maxLat - bounds.minLat) / Math.sqrt(numChunks);
    const lngStep = (bounds.maxLng - bounds.minLng) / Math.sqrt(numChunks);
    
    const chunks: Polygon[] = [];
    
    for (let i = 0; i < Math.sqrt(numChunks); i++) {
      for (let j = 0; j < Math.sqrt(numChunks); j++) {
        const chunkMinLat = bounds.minLat + i * latStep;
        const chunkMaxLat = bounds.minLat + (i + 1) * latStep;
        const chunkMinLng = bounds.minLng + j * lngStep;
        const chunkMaxLng = bounds.minLng + (j + 1) * lngStep;
        
        // Create chunk polygon (simplified as rectangle)
        const chunk: Polygon = {
          id: `${polygon.id}-chunk-${i}-${j}`,
          type: 'polygon',
          name: `${polygon.name} - Chunk ${i}-${j}`,
          colour: polygon.colour,
          description: `Chunk of ${polygon.name}`,
          opacity: polygon.opacity,
          borderColour: polygon.borderColour,
          borderWidth: polygon.borderWidth,
          isLocked: polygon.isLocked,
          coordinates: [
            [
              [chunkMinLat, chunkMinLng],
              [chunkMinLat, chunkMaxLng],
              [chunkMaxLat, chunkMaxLng],
              [chunkMaxLat, chunkMinLng],
              [chunkMinLat, chunkMinLng],
            ],
          ],
        };
        
        chunks.push(chunk);
      }
    }
    
    return chunks;
  }

  /**
   * Merge multiple transit data results into one
   */
  private mergeTransitData(dataArray: OSMTransitData[]): OSMTransitData {
    const merged: OSMTransitData = {
      stops: {
        bus: [],
        train: [],
        tram: [],
        subway: [],
      },
      routes: {
        bus: [],
        train: [],
        tram: [],
        subway: [],
      },
      infrastructure: {
        stations: [],
        platforms: [],
        shelters: [],
      },
      coverage: {
        stopDensity: 0,
        routeDensity: 0,
        accessibilityScore: 0,
      },
    };
    
    // Merge stops
    dataArray.forEach(data => {
      Object.keys(data.stops).forEach(stopType => {
        const key = stopType as keyof typeof data.stops;
        merged.stops[key].push(...data.stops[key]);
      });
    });
    
    // Merge routes
    dataArray.forEach(data => {
      Object.keys(data.routes).forEach(routeType => {
        const key = routeType as keyof typeof data.routes;
        merged.routes[key].push(...data.routes[key]);
      });
    });
    
    // Merge infrastructure
    dataArray.forEach(data => {
      merged.infrastructure.stations.push(...data.infrastructure.stations);
      merged.infrastructure.platforms.push(...data.infrastructure.platforms);
      merged.infrastructure.shelters.push(...data.infrastructure.shelters);
    });
    
    // Recalculate coverage metrics
    merged.coverage = this.calculateCoverageMetrics(merged.stops, merged.routes);
    
    return merged;
  }

  /**
   * Generate a deterministic cache key based on polygon
   */
  private generateCacheKey(polygon: Polygon): string {
    const coordsString = JSON.stringify(polygon.coordinates);
    const hash = this.simpleHash(coordsString);
    return `osm_transit_${hash}`;
  }

  /**
   * Simple hash function for cache keys
   */
  private simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * Get data from cache if available and not expired
   */
  private getFromCache(key: string): OSMTransitData | string | null {
    const cached = this.cache.get(key);
    if (!cached) return null;

    const now = Date.now();
    if (now - cached.timestamp > this.cacheConfig.ttl) {
      this.cache.delete(key);
      return null;
    }

    return cached.data;
  }

  /**
   * Get OSM transit data from cache (typed method)
   */
  private getTransitDataFromCache(key: string): OSMTransitData | null {
    const cached = this.getFromCache(key);
    return cached && typeof cached === 'object' ? cached as OSMTransitData : null;
  }

  /**
   * Get address data from cache (typed method)
   */
  private getAddressFromCache(key: string): string | null {
    const cached = this.getFromCache(key);
    return cached && typeof cached === 'string' ? cached : null;
  }

  /**
   * Store data in cache with timestamp
   */
  private setCache(key: string, data: OSMTransitData | string): void {
    // Implement LRU cache eviction if cache is full
    if (this.cache.size >= this.cacheConfig.maxSize) {
      const oldestKey: string | undefined = this.cache.keys().next().value;
      if (typeof oldestKey === 'string') {
        this.cache.delete(oldestKey);
      }
    }

    this.cache.set(key, {
      data,
      timestamp: Date.now(),
    });
  }

  /**
   * Check and enforce rate limits
   */
  private async checkRateLimits(): Promise<void> {
    const now = Date.now();

    // Reset counters if needed
    if (now - this.rateLimitTracker.lastMinuteReset > 60000) {
      this.rateLimitTracker.requestsThisMinute = 0;
      this.rateLimitTracker.lastMinuteReset = now;
    }

    if (now - this.rateLimitTracker.lastHourReset > 3600000) {
      this.rateLimitTracker.requestsThisHour = 0;
      this.rateLimitTracker.lastHourReset = now;
    }

    // Check limits
    if (this.rateLimitTracker.requestsThisMinute >= this.rateLimit.requestsPerMinute) {
      const waitTime = 60000 - (now - this.rateLimitTracker.lastMinuteReset);
      console.log(`Rate limit reached. Waiting ${waitTime}ms`);
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }

    if (this.rateLimitTracker.requestsThisHour >= this.rateLimit.requestsPerHour) {
      const waitTime = 3600000 - (now - this.rateLimitTracker.lastHourReset);
      console.log(`Hourly rate limit reached. Waiting ${waitTime}ms`);
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }

    // Increment counters
    this.rateLimitTracker.requestsThisMinute++;
    this.rateLimitTracker.requestsThisHour++;
  }

  /**
   * Calculate bounding box for polygon
   */
  private calculatePolygonBounds(polygon: Polygon): {
    minLat: number;
    maxLat: number;
    minLng: number;
    maxLng: number;
  } {
    let minLat = Infinity;
    let maxLat = -Infinity;
    let minLng = Infinity;
    let maxLng = -Infinity;

    // Iterate through all coordinates in the polygon
    for (const ring of polygon.coordinates) {
      for (const [lat, lng] of ring) {
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
        minLng = Math.min(minLng, lng);
        maxLng = Math.max(maxLng, lng);
      }
    }

    return { minLat, maxLat, minLng, maxLng };
  }

  /**
   * Fetch transit data from OpenStreetMap Overpass API
   */
  private async fetchTransitData(
    bounds: { minLat: number; maxLat: number; minLng: number; maxLng: number }
  ): Promise<OSMTransitData> {
    const query = this.buildOverpassQuery(bounds);
    const baseUrls = Array.from(new Set([this.baseUrl, ...this.fallbackBaseUrls]));
    const errors: string[] = [];

    for (const baseUrl of baseUrls) {
      const url = `${baseUrl.replace(/\/+$/, '')}/api/interpreter`;
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Accept': 'application/json',
          },
          body: `data=${encodeURIComponent(query)}`,
          signal: AbortSignal.timeout(this.timeout),
        });

        if (!response.ok) {
          errors.push(`${url} -> ${response.status} ${response.statusText}`);
          continue;
        }

        const rawData = await response.json();
        return this.transformOSMResponse(rawData);
      } catch (error) {
        errors.push(`${url} -> ${error instanceof Error ? error.message : 'Unknown network error'}`);
      }
    }

    throw new Error(`OSM API failed across all endpoints: ${errors.join(' | ')}`);
  }

  /**
   * Build Overpass QL query for transit infrastructure
   */
  private buildOverpassQuery(bounds: { minLat: number; maxLat: number; minLng: number; maxLng: number }): string {
    const bbox = `${bounds.minLat},${bounds.minLng},${bounds.maxLat},${bounds.maxLng}`;

    // Stops-only query — kept fast (15s) so the coverage pipeline isn't gated
    // by the heavier route-relations query. Keep free of inline comments
    // because Overpass parsers can reject them.
    const query = `
      [out:json][timeout:15];
      (
        node["highway"="bus_stop"](${bbox});
      );
      out body;
    `;
    return query;
  }

  /**
   * Routes-only Overpass query: PTv2 route relations + their route_master
   * parents, with member ways geometry inlined via `out body geom`. Routes
   * are fetched in parallel with stops by callers that want to stream the
   * UI as data arrives, so this query carries its own (longer) timeout.
   */
  private buildRouteOverpassQuery(bounds: { minLat: number; maxLat: number; minLng: number; maxLng: number }): string {
    const bbox = `${bounds.minLat},${bounds.minLng},${bounds.maxLat},${bounds.maxLng}`;
    const query = `
      [out:json][timeout:90];
      (
        relation["type"="route"]["route"~"^(bus|tram|subway|train|light_rail|trolleybus)$"](${bbox});
        relation["type"="route_master"]["route_master"~"^(bus|tram|subway|train|light_rail|trolleybus)$"](${bbox});
      );
      out body geom;
    `;
    return query;
  }

  /**
   * Transform OSM API response to our standardized format
   */
  private transformOSMResponse(rawData: any): OSMTransitData {
    console.log('OSM Response Debug:', {
      totalElements: rawData.elements?.length || 0,
      ways: rawData.elements?.filter((e: any) => e.type === 'way').length || 0,
      relations: rawData.elements?.filter((e: any) => e.type === 'relation').length || 0,
      nodes: rawData.elements?.filter((e: any) => e.type === 'node').length || 0,
    });

    // Create a map of node coordinates for geometry reconstruction
    const nodeMap = new Map<number, [number, number]>();
    rawData.elements?.forEach((element: any) => {
      if (element.type === 'node') {
        nodeMap.set(element.id, [element.lat, element.lon]);
      }
    });

    console.log('Node map size:', nodeMap.size);

    const stops: OSMTransitData['stops'] = {
      bus: [],
      train: [],
      tram: [],
      subway: [],
    };

    const routes: OSMTransitData['routes'] = {
      bus: [],
      train: [],
      tram: [],
      subway: [],
    };

    const infrastructure: OSMTransitData['infrastructure'] = {
      stations: [],
      platforms: [],
      shelters: [],
    };

    // Build a route_master → tags lookup so child route relations can inherit
    // colour / operator / interval / opening_hours / wheelchair / network when
    // those tags are missing on the variant itself.
    const routeMasterTagsByChildId = new Map<number, Record<string, string>>();
    if (rawData.elements) {
      for (const element of rawData.elements) {
        if (element.type === 'relation' && element.tags?.type === 'route_master') {
          (element.members || []).forEach((m: any) => {
            if (m.type === 'relation' && typeof m.ref === 'number') {
              routeMasterTagsByChildId.set(m.ref, element.tags as Record<string, string>);
            }
          });
        }
      }
    }

    // Process elements from OSM response
    let routeRelationsTotal = 0;
    let routeRelationsWithGeometry = 0;
    if (rawData.elements) {
      for (const element of rawData.elements) {
        if (element.type === 'node') {
          this.processNode(element, stops, infrastructure);
        } else if (element.type === 'way') {
          this.processWay(element, routes, nodeMap);
        } else if (element.type === 'relation' && element.tags?.type === 'route') {
          routeRelationsTotal += 1;
          const emitted = this.processRouteRelation(element, routes, routeMasterTagsByChildId);
          if (emitted) routeRelationsWithGeometry += 1;
        }
      }
    }
    console.log('OSM route relations parsed:', { total: routeRelationsTotal, withGeometry: routeRelationsWithGeometry });

    // Calculate coverage metrics
    const coverage = this.calculateCoverageMetrics(stops, routes);

    return {
      stops,
      routes,
      infrastructure,
      coverage,
    };
  }

  /**
   * Process OSM node elements
   */
  private processNode(
    node: any,
    stops: OSMTransitData['stops'],
    infrastructure: OSMTransitData['infrastructure']
  ): void {
    const tags = node.tags || {};
    const coords: [number, number] = [node.lat, node.lon];

    // Bus stops
    if (tags.highway === 'bus_stop') {
      stops.bus.push({
        id: node.id.toString(),
        name: tags.name || tags.ref || `Bus Stop ${node.id}`,
        type: 'bus',
        coordinates: coords,
        routes: [],
        wheelchair: tags.wheelchair === 'yes',
        shelter: tags.shelter === 'yes',
        realtime: tags.realtime === 'yes',
      });
    }

    // Train stations
    if (tags.railway === 'station') {
      stops.train.push({
        id: node.id.toString(),
        name: tags.name || `Train Station ${node.id}`,
        type: 'train',
        coordinates: coords,
        routes: [],
        wheelchair: tags.wheelchair === 'yes',
        shelter: true,
        realtime: tags.realtime === 'yes',
      });

      infrastructure.stations.push({
        id: node.id.toString(),
        name: tags.name || `Train Station ${node.id}`,
        type: 'train',
        coordinates: coords,
        platforms: parseInt(tags.platforms) || 1,
        accessibility: tags.wheelchair === 'yes',
      });
    }

    // Tram stops
    if (tags.railway === 'tram_stop') {
      stops.tram.push({
        id: node.id.toString(),
        name: tags.name || `Tram Stop ${node.id}`,
        type: 'tram',
        coordinates: coords,
        routes: [],
        wheelchair: tags.wheelchair === 'yes',
        shelter: tags.shelter === 'yes',
        realtime: tags.realtime === 'yes',
      });
    }

    // Subway stations
    if (tags.railway === 'subway_entrance') {
      stops.subway.push({
        id: node.id.toString(),
        name: tags.name || `Subway Station ${node.id}`,
        type: 'subway',
        coordinates: coords,
        routes: [],
        wheelchair: tags.wheelchair === 'yes',
        shelter: true,
        realtime: tags.realtime === 'yes',
      });
    }
  }

  /**
   * Process OSM way elements
   */
  // Currently a no-op for routes: PTv2 represents routes as relations, and the
  // way-as-route path was emitting noisy duplicates from legacy tags. Kept so
  // future per-way infrastructure (e.g. rail tracks) can hook in cleanly.
  private processWay(_way: any, _routes: OSMTransitData['routes'], _nodeMap: Map<number, [number, number]>): void {
    return;
  }

  /**
   * Parse a PTv2 `type=route` relation into a fully populated OSMRoute and push
   * it into the appropriate bucket. Returns true if a route with valid geometry
   * was emitted. Geometry is reconstructed by chaining the inline `geometry`
   * arrays returned by Overpass `out body geom`; reversed member ways are
   * detected via shared endpoints and flipped before concatenation.
   */
  private processRouteRelation(
    relation: any,
    routes: OSMTransitData['routes'],
    routeMasterTagsByChildId: Map<number, Record<string, string>>,
  ): boolean {
    const tags = (relation.tags || {}) as Record<string, string>;
    const osmMode = tags.route as
      | 'bus' | 'tram' | 'subway' | 'train' | 'light_rail' | 'trolleybus' | undefined;
    if (!osmMode) return false;

    // Collapse extended modes into existing buckets so downstream consumers
    // don't need to know about light_rail / trolleybus yet.
    const bucket: keyof OSMTransitData['routes'] =
      osmMode === 'light_rail' ? 'train'
      : osmMode === 'trolleybus' ? 'bus'
      : osmMode;
    if (!routes[bucket]) return false;

    const parentTags = routeMasterTagsByChildId.get(relation.id) || {};
    const tagOrParent = (key: string): string | undefined =>
      tags[key] || parentTags[key] || undefined;

    // Pull member ways (empty role) for geometry and stop nodes (stop* roles).
    const memberWays: [number, number][][] = [];
    const stopRefs: string[] = [];
    (relation.members || []).forEach((m: any) => {
      const role: string = m.role || '';
      if (m.type === 'way' && role === '' && Array.isArray(m.geometry) && m.geometry.length >= 2) {
        memberWays.push(m.geometry.map((g: any) => [g.lat, g.lon] as [number, number]));
      } else if (m.type === 'node' && (role === 'stop' || role === 'stop_entry_only' || role === 'stop_exit_only')) {
        stopRefs.push(String(m.ref));
      }
    });

    const geometry = chainOsmWayGeometries(memberWays);
    if (geometry.length < 2) return false;

    const ref = tagOrParent('ref');
    const name = tags.name || tagOrParent('name') || (ref ? `Route ${ref}` : `Route ${relation.id}`);
    const wheelchairRaw = tagOrParent('wheelchair');
    const wheelchair: OSMRoute['wheelchair'] =
      wheelchairRaw === 'yes' || wheelchairRaw === 'no' || wheelchairRaw === 'limited'
        ? wheelchairRaw
        : (wheelchairRaw ? 'unknown' : undefined);

    const route: OSMRoute = {
      id: relation.id.toString(),
      name,
      type: bucket,
      osmMode,
      operator: tagOrParent('operator') || 'Unknown',
      frequency: parseInt(tagOrParent('frequency') || '0', 10) || 0,
      stops: stopRefs,
      geometry,
      ref,
      from: tags.from,
      to: tags.to,
      via: tags.via,
      network: tagOrParent('network'),
      colour: normalizeOsmColour(tagOrParent('colour') || tagOrParent('color')),
      textColour: normalizeOsmColour(tagOrParent('text_colour') || tagOrParent('text_color')),
      openingHours: tagOrParent('opening_hours'),
      interval: tagOrParent('interval'),
      duration: tagOrParent('duration'),
      wheelchair,
      routeMasterId: parentTags && Object.keys(parentTags).length > 0
        ? routeMasterIdFor(relation.id, routeMasterTagsByChildId)
        : undefined,
    };

    routes[bucket].push(route);
    return true;
  }

  /**
   * Calculate coverage metrics
   */
  private calculateCoverageMetrics(
    stops: OSMTransitData['stops'],
    routes: OSMTransitData['routes']
  ): OSMTransitData['coverage'] {
    const totalStops = Object.values(stops).flat().length;
    const totalRoutes = Object.values(routes).flat().length;
    
    // These would need to be calculated based on the actual polygon area
    const areaKm2 = 1; // Placeholder - would calculate from polygon
    
    return {
      stopDensity: totalStops / areaKm2,
      routeDensity: totalRoutes / areaKm2,
      accessibilityScore: Math.min(totalStops / 100, 1), // Normalized score
    };
  }

  /**
   * Clear the cache
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): {
    size: number;
    maxSize: number;
    hitRate: number;
  } {
    return {
      size: this.cache.size,
      maxSize: this.cacheConfig.maxSize,
      hitRate: 0, // Would need to track hits/misses
    };
  }

  /**
   * Get address for coordinates using reverse geocoding
   * 
   * Converts geographic coordinates to human-readable addresses using the Google Maps
   * Geocoding API (through the Atlas backend) with caching.
   * 
   * @param coords - Array containing [longitude, latitude] coordinates
   * @returns Promise<string> - Human-readable address string
   * 
   * @example
   * ```typescript
   * const address = await osmService.getAddressForCoordinates([-31.9505, 115.8605]);
   * console.log(address); // "123 Main St" or "Main St & Oak Ave"
   * ```
   * 
   * @remarks
   * - Uses 6-level priority system for address formatting
   * - Implements caching with 1-hour TTL
   * - Respects client-side rate limits
   * - Falls back to "Meeting Point" on complete failure
   * - Tracks analytics for performance monitoring
   */
  async getAddressForCoordinates(coords: [number, number]): Promise<string> {
    const startTime = Date.now();
    const cacheKey = this.generateAddressCacheKey(coords);
    
    // REUSE existing cache check
    const cached = this.getAddressFromCache(cacheKey);
    if (cached) {
      logEvent({
        category: 'Address Lookup',
        action: 'AddressCacheHit',
        label: `Address lookup for coordinates ${coords[0]}, ${coords[1]}`,
        cache_key: cacheKey,
        response_time_ms: Date.now() - startTime
      });
      
      console.log('Address retrieved from cache');
      return cached;
    }
    
    // REUSE existing rate limiting
    await this.checkRateLimits();
    
    try {
      // Temporarily disable intersection detection to fix basic address lookup
      // TODO: Re-enable once Overpass query issues are resolved
      let osmRoadData = null;
      // try {
      //   osmRoadData = await this.getRoadDataForCoordinates(coords);
      // } catch (roadDataError) {
      //   console.warn('OSM road data fetch failed, proceeding with basic address lookup:', roadDataError);
      //   // Continue without intersection detection - this is not a critical failure
      // }
      
      const address = await this.reverseGeocode(coords);
      // REUSE existing cache storage
      this.setCache(cacheKey, address);
      
      const endTime = Date.now();
      const duration = endTime - startTime;
      
      logEvent({
        category: 'Address Lookup',
        action: 'AddressFetched',
        label: `Address lookup for coordinates ${coords[0]}, ${coords[1]}`,
        coordinates: coords,
        address_result: address,
        cache_key: cacheKey,
        response_time_ms: duration,
        intersection_detected: osmRoadData ? detectIntersection(coords, osmRoadData) : false,
        osm_road_data_available: !!osmRoadData
      });
      
      console.log('Address fetched successfully:', address);
      return address;
    } catch (error) {
      const endTime = Date.now();
      const duration = endTime - startTime;
      
      logEvent({
        category: 'Address Lookup',
        action: 'AddressError',
        label: `Address lookup failed for coordinates ${coords[0]}, ${coords[1]}`,
        coordinates: coords,
        error_message: error instanceof Error ? error.message : 'Unknown error',
        cache_key: cacheKey,
        response_time_ms: duration
      });
      
      // REUSE existing error handling
      console.warn('Address lookup failed:', error);
      return 'Meeting Point'; // Fallback
    }
  }

  /**
   * Generate cache key for address lookups
   */
  private generateAddressCacheKey(coords: [number, number]): string {
    const [lat, lng] = coords;
    const roundedLat = Math.round(lat * 1000000) / 1000000; // 6 decimal places
    const roundedLng = Math.round(lng * 1000000) / 1000000; // 6 decimal places
    return `address_${roundedLat}_${roundedLng}`;
  }

  /**
   * Perform reverse geocoding using Google Maps Geocoding API via the Atlas backend
   */
  private async reverseGeocode(coords: [number, number]): Promise<string> {
    const [lat, lng] = coords;
    const response = await atlasFetch(`${getAtlasApiBaseUrl()}/api/google-maps/reverse-geocode`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ latitude: lat, longitude: lng }),
      signal: AbortSignal.timeout(this.timeout),
    });

    if (!response.ok) {
      throw new Error(`Google Maps API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    
    if (data.status !== 'OK' || !data.results || data.results.length === 0) {
      throw new Error(`Google Maps geocoding failed: ${data.status}`);
    }

    // Extract components using utility function
    const components = extractGoogleComponents(data);
    
    // Format using utility function
    const formatted = formatAddressFromComponents(components);
    
    // Sanitize and validate
    return sanitizeAddress(formatted) || 'Meeting Point';
  }

  /**
   * Get OSM road data for intersection detection
   */
  async getRoadDataForCoordinates(coords: [number, number]): Promise<any> {
    const [lng, lat] = coords;
    
    // Create a small bounding box around the coordinates (about 100m radius)
    const bbox = [
      lng - 0.001, // West
      lat - 0.001, // South
      lng + 0.001, // East
      lat + 0.001  // North
    ].join(',');

    const query = `[out:json][timeout:10];
(
  way["highway"~"^(primary|secondary|tertiary|residential|unclassified|service)$"](bbox:${bbox});
);
out geom;`;

    try {
      console.log('Fetching OSM road data for intersection detection:', { coords, bbox, query });
      
      const response = await fetch(`https://overpass-api.de/api/interpreter?data=${encodeURIComponent(query)}`);
      if (!response.ok) {
        const errorText = await response.text();
        console.error('OSM road data request failed:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText,
          query: query,
          bbox: bbox
        });
        throw new Error(`OSM road data request failed: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('OSM road data fetched successfully:', {
        coords,
        elementsCount: data.elements?.length || 0,
        data: data
      });
      return data;
    } catch (error) {
      console.warn('Failed to fetch OSM road data for intersection detection:', {
        coords,
        bbox,
        query,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      return null;
    }
  }

} 
